import React, { useState, useEffect } from 'react';
import { 
    Container, 
    Typography, 
    Box, 
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    CircularProgress,
    Chip,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    MenuItem,
    Alert,
    Snackbar
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { getFirestore, collection, getDocs, query, orderBy, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';

const firestore = getFirestore();
const unidades = ['KM', 'Pcs', 'EACH', 'Core'];

function ListServicios() {
    const [servicios, setServicios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [editingServicio, setEditingServicio] = useState(null);
    const [formData, setFormData] = useState({
        codigo: '',
        descripcion: '',
        unidad: 'Pcs',
        zona1: '',
        zona2: '',
        zona3: ''
    });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, servicio: null });

    useEffect(() => {
        fetchServicios();
    }, []);

    const fetchServicios = async () => {
        try {
            setLoading(true);
            const q = query(
                collection(firestore, 'precios_huawei'),
                orderBy('id', 'asc')
            );
            const querySnapshot = await getDocs(q);
            const serviciosData = [];
            
            querySnapshot.forEach((docSnap) => {
                serviciosData.push({ ...docSnap.data(), docId: docSnap.id });
            });
            
            setServicios(serviciosData);
        } catch (error) {
            console.error('Error al cargar servicios:', error);
            showSnackbar('Error al cargar los servicios', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (servicio = null) => {
        if (servicio) {
            setEditingServicio(servicio);
            setFormData({
                codigo: servicio.codigo || '',
                descripcion: servicio.descripcion || '',
                unidad: servicio.unidad || 'Pcs',
                zona1: servicio.zona1 || '',
                zona2: servicio.zona2 || '',
                zona3: servicio.zona3 || ''
            });
        } else {
            setEditingServicio(null);
            setFormData({
                codigo: '',
                descripcion: '',
                unidad: 'Pcs',
                zona1: '',
                zona2: '',
                zona3: ''
            });
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingServicio(null);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async () => {
        try {
            // Validaciones
            if (!formData.codigo || !formData.descripcion) {
                showSnackbar('Código y descripción son obligatorios', 'warning');
                return;
            }

            const dataToSave = {
                codigo: formData.codigo,
                descripcion: formData.descripcion,
                unidad: formData.unidad,
                zona1: formData.zona1 ? parseInt(formData.zona1) : null,
                zona2: formData.zona2 ? parseInt(formData.zona2) : null,
                zona3: formData.zona3 ? parseInt(formData.zona3) : null
            };

            if (editingServicio) {
                // Actualizar servicio existente
                const docRef = doc(firestore, 'precios_huawei', editingServicio.docId);
                await updateDoc(docRef, dataToSave);
                showSnackbar('Servicio actualizado exitosamente', 'success');
            } else {
                // Crear nuevo servicio
                // Obtener el ID más alto para asignar el siguiente
                const maxId = servicios.length > 0 
                    ? Math.max(...servicios.map(s => parseInt(s.id) || 0))
                    : 0;
                
                dataToSave.id = (maxId + 1).toString();
                await addDoc(collection(firestore, 'precios_huawei'), dataToSave);
                showSnackbar('Servicio creado exitosamente', 'success');
            }

            handleCloseDialog();
            fetchServicios();
        } catch (error) {
            console.error('Error al guardar servicio:', error);
            showSnackbar('Error al guardar el servicio', 'error');
        }
    };

    const handleDelete = async (servicio) => {
        setDeleteConfirm({ open: true, servicio });
    };

    const confirmDelete = async () => {
        try {
            const docRef = doc(firestore, 'precios_huawei', deleteConfirm.servicio.docId);
            await deleteDoc(docRef);
            showSnackbar('Servicio eliminado exitosamente', 'success');
            setDeleteConfirm({ open: false, servicio: null });
            fetchServicios();
        } catch (error) {
            console.error('Error al eliminar servicio:', error);
            showSnackbar('Error al eliminar el servicio', 'error');
        }
    };

    const showSnackbar = (message, severity) => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const formatPrice = (price) => {
        if (price === null || price === undefined) return '-';
        return new Intl.NumberFormat('es-PY', {
            style: 'currency',
            currency: 'PYG',
            minimumFractionDigits: 0
        }).format(price);
    };

    const filteredServicios = servicios.filter(servicio => {
        const searchLower = searchTerm.toLowerCase();
        return (
            servicio.codigo?.toLowerCase().includes(searchLower) ||
            servicio.descripcion?.toLowerCase().includes(searchLower) ||
            servicio.unidad?.toLowerCase().includes(searchLower)
        );
    });

    if (loading) {
        return (
            <Container maxWidth="xl">
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl">
            <Box sx={{ mt: 4, mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4" component="h1">
                        Servicios - Huawei
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog()}
                    >
                        Nuevo Servicio
                    </Button>
                </Box>
                
                <Box sx={{ mb: 3 }}>
                    <TextField
                        fullWidth
                        label="Buscar por código, descripción o unidad"
                        variant="outlined"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Ej: 8812370586, Relevamiento, KM..."
                    />
                </Box>

                <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                    <Chip label={`Total: ${filteredServicios.length} servicios`} color="primary" />
                </Box>

                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Código</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Descripción</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Unidad</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>Zona 1</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>Zona 2</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>Zona 3</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Acciones</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredServicios.map((servicio) => (
                                <TableRow 
                                    key={servicio.docId}
                                    sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}
                                >
                                    <TableCell>{servicio.id}</TableCell>
                                    <TableCell>{servicio.codigo}</TableCell>
                                    <TableCell sx={{ maxWidth: 400 }}>{servicio.descripcion}</TableCell>
                                    <TableCell>
                                        <Chip label={servicio.unidad} size="small" variant="outlined" />
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'right' }}>
                                        {formatPrice(servicio.zona1)}
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'right' }}>
                                        {formatPrice(servicio.zona2)}
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'right' }}>
                                        {formatPrice(servicio.zona3)}
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>
                                        <IconButton
                                            color="primary"
                                            size="small"
                                            onClick={() => handleOpenDialog(servicio)}
                                            title="Editar"
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            color="error"
                                            size="small"
                                            onClick={() => handleDelete(servicio)}
                                            title="Eliminar"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {filteredServicios.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                            No se encontraron resultados{searchTerm && ` para "${searchTerm}"`}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Dialog para Crear/Editar */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editingServicio ? 'Editar Servicio' : 'Nuevo Servicio'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Código *"
                                    name="codigo"
                                    value={formData.codigo}
                                    onChange={handleInputChange}
                                    placeholder="Ej: 8812370586"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Unidad *"
                                    name="unidad"
                                    value={formData.unidad}
                                    onChange={handleInputChange}
                                >
                                    {unidades.map((unidad) => (
                                        <MenuItem key={unidad} value={unidad}>
                                            {unidad}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Descripción *"
                                    name="descripcion"
                                    value={formData.descripcion}
                                    onChange={handleInputChange}
                                    multiline
                                    rows={3}
                                    placeholder="Descripción del servicio"
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Precio Zona 1"
                                    name="zona1"
                                    type="number"
                                    value={formData.zona1}
                                    onChange={handleInputChange}
                                    placeholder="0"
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Precio Zona 2"
                                    name="zona2"
                                    type="number"
                                    value={formData.zona2}
                                    onChange={handleInputChange}
                                    placeholder="0"
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Precio Zona 3"
                                    name="zona3"
                                    type="number"
                                    value={formData.zona3}
                                    onChange={handleInputChange}
                                    placeholder="0"
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>
                        Cancelar
                    </Button>
                    <Button 
                        onClick={handleSubmit} 
                        variant="contained"
                        color="primary"
                    >
                        {editingServicio ? 'Actualizar' : 'Crear'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog de Confirmación de Eliminación */}
            <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, servicio: null })}>
                <DialogTitle>Confirmar Eliminación</DialogTitle>
                <DialogContent>
                    <Typography>
                        ¿Estás seguro de que deseas eliminar el servicio "{deleteConfirm.servicio?.descripcion}"?
                    </Typography>
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        Esta acción no se puede deshacer.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirm({ open: false, servicio: null })}>
                        Cancelar
                    </Button>
                    <Button onClick={confirmDelete} color="error" variant="contained">
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar para notificaciones */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
}

export default ListServicios;
